import React, { useState } from 'react';
import api from '../../services/api';
import './Admin.css';

const NewsManager = () => {
  const [news, setNews] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const fetchNews = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await api.get('/news/fetch');
      
      if (response.data.success) {
        setNews(response.data.articles);
        setMessage(`${response.data.articles.length} nieuwsberichten opgehaald${response.data.isDemo ? ' (DEMO MODE)' : ''}`);
        setMessageType('success');
        
        // Auto-select first 3 articles
        if (response.data.articles.length > 0) {
          setSelectedArticles(response.data.articles.slice(0, 3));
        }
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Fout bij ophalen nieuws');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const toggleArticle = (article) => {
    const isSelected = selectedArticles.find(a => a.title === article.title);
    
    if (isSelected) {
      setSelectedArticles(selectedArticles.filter(a => a.title !== article.title));
    } else {
      if (selectedArticles.length < 5) {
        setSelectedArticles([...selectedArticles, article]);
      } else {
        setMessage('Maximaal 5 nieuwsberichten kunnen geselecteerd worden');
        setMessageType('error');
      }
    }
  };

  const broadcastNews = async () => {
    if (selectedArticles.length === 0) {
      setMessage('Selecteer minimaal 1 nieuwsbericht');
      setMessageType('error');
      return;
    }

    setBroadcasting(true);
    setMessage('');

    try {
      const response = await api.post('/news/broadcast', {
        articles: selectedArticles
      });

      if (response.data.success) {
        setMessage(`Nieuws in queue! Wordt afgespeeld na het huidige nummer. (Positie: ${response.data.queuePosition})`);
        setMessageType('success');
        setSelectedArticles([]);
        setNews([]);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Fout bij uitzenden nieuws');
      setMessageType('error');
    } finally {
      setBroadcasting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', { 
      hour: '2-digit', 
      minute: '2-digit',
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <div className="news-manager-page">
      <div className="page-header">
        <h1><i className="fas fa-newspaper"></i> Nieuws Uitzenden</h1>
        <p className="page-subtitle">Haal actueel nieuws op en stuur het live de radio op via TTS</p>
      </div>

      <div className="news-actions">
        <button 
          onClick={fetchNews} 
          className="btn-primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Nieuws ophalen...
            </>
          ) : (
            <>
              <i className="fas fa-sync"></i> Haal Actueel Nieuws Op
            </>
          )}
        </button>

        {selectedArticles.length > 0 && (
          <button 
            onClick={broadcastNews} 
            className="btn-success"
            disabled={broadcasting}
          >
            {broadcasting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Aan het uitzenden...
              </>
            ) : (
              <>
                <i className="fas fa-broadcast-tower"></i> Uitzenden ({selectedArticles.length} geselecteerd)
              </>
            )}
          </button>
        )}
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          <i className={`fas fa-${messageType === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message}
        </div>
      )}

      {news.length > 0 && (
        <div className="news-list">
          <h3>📰 Selecteer Nieuwsberichten ({selectedArticles.length}/5)</h3>
          <p className="hint">Klik op een bericht om het te selecteren/deselecteren</p>
          
          {news.map((article, index) => {
            const isSelected = selectedArticles.find(a => a.title === article.title);
            
            return (
              <div 
                key={index} 
                className={`news-item ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleArticle(article)}
              >
                <div className="news-item-header">
                  <div className="news-meta">
                    <span className="news-source">
                      <i className="fas fa-building"></i> {article.source.name}
                    </span>
                    <span className="news-date">
                      <i className="fas fa-clock"></i> {formatDate(article.publishedAt)}
                    </span>
                  </div>
                  {isSelected && (
                    <div className="selected-badge">
                      <i className="fas fa-check-circle"></i> Geselecteerd
                    </div>
                  )}
                </div>
                <h4>{article.title}</h4>
                {article.description && (
                  <p className="news-description">{article.description}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="info-box">
        <h3><i className="fas fa-info-circle"></i> Hoe werkt het?</h3>
        <ul>
          <li><strong>Stap 1:</strong> Klik op "Haal Actueel Nieuws Op" om de laatste nieuwsberichten te laden</li>
          <li><strong>Stap 2:</strong> Selecteer maximaal 5 nieuwsberichten die je wilt uitzenden</li>
          <li><strong>Stap 3:</strong> Klik op "Uitzenden" om de AI een nieuwsbericht te laten genereren</li>
          <li><strong>Timing:</strong> Het nieuws wordt via TTS voorgelezen na het huidige nummer</li>
          <li><strong>Bronnen:</strong> NOS, NU.nl, AD.nl (via RSS feeds - 100% GRATIS)</li>
          <li><strong>Taal:</strong> Alle nieuwsberichten zijn in het Nederlands</li>
        </ul>
      </div>
    </div>
  );
};

export default NewsManager;
